import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import {
  planSelectionGuard,
  subscriptionGuard,
} from './core/guards/subscription.guard';
import { emailVerifiedGuard } from './core/guards/email-verified.guard';
import { featureFlagGuard } from './core/guards/feature-flag.guard';
import { AuthFacade } from './core/auth/auth.facade';
import { SettingsLayoutComponent } from './layout/settings-layout/settings-layout.component';

export const routes: Routes = [
  {
    // First on purpose: the emailed link carries `?token=`, which the root
    // share-invitation route below also matches on.
    path: 'verify-email',
    loadChildren: () => import('./features/verify-email/verify-email.routes'),
  },
  {
    path: '',
    canMatch: [() => !!inject(Router).getCurrentNavigation()?.extractedUrl.queryParams['code']],
    loadChildren: () => import('./features/auth-console/auth-console.routes'),
  },
  {
    path: '',
    canMatch: [() => !!inject(Router).getCurrentNavigation()?.extractedUrl.queryParams['token']],
    loadChildren: () => import('./features/templates/share-invitation.routes'),
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  {
    path: 'login',
    loadChildren: () => import('./features/login/login.routes'),
  },
  {
    path: 'resetpassword',
    loadChildren: () =>
      import('./features/forgot-password/forgot-password.routes'),
  },
  {
    path: 'reset-password',
    loadChildren: () =>
      import('./features/reset-password/reset-password.routes'),
  },
  {
    path: 'oauth/:provider/callback',
    loadChildren: () =>
      import('./features/oauth-callback/oauth-callback.routes'),
  },
  {
    path: 'organization',
    loadChildren: () => import('./features/organizations/organizations.routes'),
  },
  {
    path: 'privacy-policy',
    loadChildren: () => import('./features/privacy-policy/privacy-policy.routes'),
  },
  {
    path: 'sign',
    loadComponent: () =>
      import('./layout/signing-layout/signing-layout.component').then(
        (m) => m.SigningLayoutComponent,
      ),
    children: [
      {
        path: '',
        loadChildren: () => import('./features/pdf-viewer/pdf-viewer.routes'),
      },
    ],
  },
  {
    path: 'signing-success',
    loadChildren: () =>
      import('./features/signing-success/signing-success.routes'),
  },
  {
    // Paywall for organizations without a subscription. Outside `/admin` on
    // purpose — the app shell isn't reachable until a plan is paid for.
    path: 'select-plan',
    canActivate: [AuthGuard, emailVerifiedGuard, planSelectionGuard],
    loadChildren: () => import('./features/select-plan/select-plan.routes'),
  },
  {
    path: 'admin',
    loadComponent: () =>
      import('./layout/app-layout/app-layout.component').then(
        ({ AppLayoutComponent }) => AppLayoutComponent,
      ),
    canActivate: [AuthGuard, emailVerifiedGuard, subscriptionGuard],
    canActivateChild: [AuthGuard, emailVerifiedGuard, subscriptionGuard],
    // Pairs with `onSameUrlNavigation: 'reload'`: an organization switch that
    // lands on the URL it started from still re-runs the guards.
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: 'settings',
        component: SettingsLayoutComponent,
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [() => {
              const router = inject(Router);
              const section = inject(AuthFacade).permissions().canManage ? 'users' : 'brands';
              return router.createUrlTree(['/admin/settings', section]);
            }],
            children: [],
          },
          {
            path: 'users',
            loadChildren: () => import('./features/users/users.routes'),
          },
          // {
          //   path: 'teams',
          //   loadChildren: () => import('./features/teams/teams.routes'),
          // },
          {
            path: 'brands',
            loadChildren: () => import('./features/brands/brands.routes'),
          },
          {
            path: 'contacts',
            loadChildren: () => import('./features/contacts/contacts.routes'),
          },
          {
            path: 'plans',
            loadChildren: () => import('./features/plans/plans.routes'),
          },
          {
            path: 'organization',
            loadChildren: () => import('./features/organization-settings/organization-settings.routes'),
          },
          {
            path: 'api-keys',
            loadChildren: () => import('./features/api-keys/api-keys.routes'),
          },
          {
            path: 'integrations',
            loadChildren: () => import('./features/integrations/integrations.routes'),
          },
        ],
      },
      {
        path: 'templates',
        loadChildren: () => import('./features/templates/templates.routes'),
      },
      {
        path: 'send-now',
        canMatch: [featureFlagGuard('send-now')],
        loadChildren: () => import('./features/templates/send-now.routes'),
      },
      {
        path: 'sign-now',
        canMatch: [featureFlagGuard('sign-now')],
        loadChildren: () => import('./features/templates/sign-now.routes'),
      },
      {
        path: 'history',
        loadChildren: () => import('./features/signing-history/signing-history.routes'),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/signature-requests/signature-requests.routes'),
      },
      {
        path: 'contacts',
        loadChildren: () => import('./features/contacts/contacts.routes'),
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./features/profile-settings/profile-settings.routes'),
      },
      {
        path: 'privacy-policy',
        loadChildren: () =>
          import('./features/privacy-policy/privacy-policy.routes'),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
